import Backbone from 'backbone';
import _ from 'underscore';
import Promise from 'bluebird';

import horizontalTemplate from '../template/layout-horizontal.pug';
import verticalTemplate from '../template/layout-vertical.pug';
import actionTemplate from '../template/action.pug';
import headerTemplate from '../template/header.pug';

/** The View class of steps wizard */
export class StepsWizard extends Backbone.View {
  /**
   *
   * @param {object} option the option.
   * @param {object} option.config the configuration to construct the steps wizard.
   * @param {object[]} option.steps the steps.
   * @param {string} option.steps[].name the name of each step.(must be unique).
   * @param {object} option.steps[].view the view instance of each step.
   * (must be backbone view instance)
   * @param {hash} [option.events] algn with http://backbonejs.org/#View-events.
   * @param {function} [option.config.layout] the pug template if you wish to have a customized
   * layout.
   * @param {Boolean} [isHorizontal=false] if set to true, the wizard will use the default
   * horizontal layout. Otherwise it will use the customized layout or default vertical layout.
   * @param {function} [actionTemplate] By default steps wizard will draw buttons according to the
   * actions of each view and hook it up with the action.method. If you want to customize the ui
   * elements instead of buttons to reflect step actions, specify this parameter.
   * @param {string} [option.currentStep] the name of the step as the starting point of the flow.
   * If not given, start from the 1st step.
   * @param {function} [headerTemplate] the template of the title.
   * @return {Void} void
   * @throws Will throw an error if you forgot to define the steps.
   * @throws Will throw an error if 2 or more steps has the same name.
   * @throws Will throw an error if option.currentStep is not a valid name.
   * @throws Will throw an error if option.steps[].view is not a backbone view.
   * @throws Will throw an error if option.layout is not a function.
   * @throws Will throw an error if option.actionTemplate is not a funcion.
   * @throws Will throw an error if option.initialState is not a object.
   */
  initialize({
    config = {},
    currentStep,
    initialState = {},
  } = {}) {
    if (_.isEmpty(config.steps)) {
      throw new Error('At least 1 step is required.');
    }

    this.visitedMatrix = {};
    this.steps = config.steps;
    this.checkSteps();

    if (_.isString(currentStep)) {
      this.fromStepIndex = _.findIndex(this.steps, { name: currentStep });

      if (this.fromStepIndex < 0) {
        throw new Error('Invalid current step name in config.');
      }
    } else {
      this.fromStepIndex = 0;
    }

    this.customizedEvents = config.events;

    if (_.isFunction(config.layout)) {
      this.template = config.layout;
    } else if (_.has(config, 'layout')) {
      throw new Error('A function is expected for custom layout.');
    } else if (config.isHorizontal) {
      this.template = horizontalTemplate;
    } else {
      this.template = verticalTemplate;
    }

    if (_.isFunction(config.actionTemplate)) {
      this.actionTemplate = config.actionTemplate;
    } else if (_.has(config, 'actionTemplate')) {
      throw new Error('A function is expected for custom actionTemplate.');
    } else {
      this.actionTemplate = actionTemplate;
    }

    if (_.isObject(initialState)) {
      this.state = initialState;
    } else {
      throw new Error('Initial state should be an object');
    }

    if (_.isFunction(config.headerTemplate)) {
      this.headerTemplate = config.headerTemplate;
    } else {
      this.headerTemplate = headerTemplate;
    }
  }

  /**
   * Check the steps configuration is valid
   *
   * @private
   * @return {string[]} the steps name list
   * @throws Will throw an error if there are more than 1 step has the duplicated name.
   */
  checkSteps() {
    if (_.chain(this.steps)
      .map(_.property('name'))
      .uniq()
      .size()
      .value() !== _.size(this.steps)
    ) {
      throw new Error('More than 1 step has the duplicated name.');
    }

    return _.each(this.steps, (step) => {
      if (!(step.view instanceof Backbone.View)) {
        throw new Error('Step view should be a Backbone view.');
      }
    }, []);
  }

  render() {
    this.$el.html(this.template({
      steps: this.steps,
      actions: this.allActions,
    }));

    this.currentStepIndex = this.fromStepIndex;

    this.delegateEvents(this.customizedEvents);

    _.each(this.steps, (step) => {
      this.listenTo(step.view, 'change:title', () => {
        if (step.name === this.currentStepName) {
          this.drawHeader();
        }
      });

      this.listenTo(step.view, 'change:action', () => {
        if (step.name === this.currentStepName) {
          this.drawActionBar();
        }
      });
    });

    return this;
  }

  get predefinedActions() {
    const processActionMethod = (resultFromMethod, target) =>
      Promise.resolve(resultFromMethod)
        .then((result) => {
          if (result) {
            this.goTo(target);
          }

          // steps may have async methods.
          // see https://msasg.visualstudio.com/Bing_Ads/_git/AdsAppUISharedComponents/pullrequest/332105#_a=overview
          return null;
        });

    return [
      {
        name: 'next',
        displayText: 'Next',
        enabled: true,
        htmlAttributes: {
          class: 'btn btn-primary',
        },
        method() {
          this.trigger('step:next', {
            currentStepName: this.currentStepName,
            destStepName: this.nextStepName,
          });

          return processActionMethod(
            this.currentStepInstance.wizardShouldGoNext(),
            this.nextStepName
          );
        },
      },
      {
        name: 'back',
        displayText: 'Back',
        enabled: true,
        htmlAttributes: {
          class: 'btn btn-default',
        },
        method() {
          this.trigger('step:back', {
            currentStepName: this.currentStepName,
            destStepName: this.previousStepName,
          });

          return processActionMethod(
            this.currentStepInstance.wizardShouldGoBack(),
            this.previousStepName
          );
        },
      },
      {
        name: 'cancel',
        displayText: 'Cancel',
        enabled: true,
        htmlAttributes: {
          class: 'btn btn-default',
        },
        method: 'cancel',
      },
      {
        name: 'save',
        displayText: 'Save',
        enabled: true,
        htmlAttributes: {
          class: 'btn btn-default',
        },
        method: 'save',
      },
    ];
  }

  get stepsCount() {
    return _.size(this.steps);
  }

  get firstStep() {
    return _.first(this.steps);
  }

  get lastStep() {
    return _.last(this.steps);
  }

  get isOnFirstStep() {
    return this.stepIndex === 0;
  }

  get isOnLastStep() {
    return this.stepIndex === this.stepsCount - 1;
  }

  get currentStepIndex() {
    return this.stepIndex;
  }

  set currentStepIndex(index) {
    if (index < 0 || index >= this.stepsCount) {
      throw new Error('step index out of range.');
    }

    this.stepIndex = index;

    const stepPageTitle = this.$find(`.step-body[data-step-name=${this.currentStepName}] > .step-page-title`);
    const allStepsPageTitle = this.$find('.step-body .step-page-title');
    allStepsPageTitle.hide();
    stepPageTitle.show();

    const stepContainer = this.$find(`.step-body[data-step-name=${this.currentStepName}] > .step-content`);
    const allStepsContainer = this.$find('.step-body .step-content');

    if (!this.visitedMatrix[this.currentStepIndex]) {
      stepContainer.html(this.currentStepInstance.$el);
      this.currentStepInstance.render();
    }

    allStepsContainer.hide();

    if (_.isFunction(this.currentStepInstance.setState)) {
      this.currentStepInstance.setState(this.state);
    } else {
      throw new Error('Lack of method: setState.');
    }

    this.currentStepInstance.willShow();

    stepContainer.show();

    this.visitedMatrix[index] = true;
    this.$el.attr('data-current-step-name', this.currentStepName);
    if (this.currentStepObject.railviewId) {
      this.$el.attr('railviewid', this.currentStepObject.railviewId);
    }

    this.drawHeader();
    this.drawActionBar();

    this.decorateElementWithStepStatus('.step-container');
  }

  get currentStepObject() {
    return this.steps[this.currentStepIndex];
  }

  get previousStepObject() {
    if (this.isOnFirstStep) {
      throw new Error('The first step does not have a previous step.');
    }

    return this.steps[this.currentStepIndex - 1];
  }

  get nextStepObject() {
    if (this.isOnLastStep) {
      throw new Error('The last step does not have a next step.');
    }

    return this.steps[this.currentStepIndex + 1];
  }

  /**
   * @returns {string} the name of the current step
   */
  get currentStepName() {
    return this.currentStepObject.name;
  }

  /**
   * @returns {object} the view instance of the current step
   */
  get currentStepInstance() {
    return this.currentStepObject.view;
  }

  /**
   * @returns {string} the name of the previous step
   */
  get previousStepName() {
    return this.previousStepObject.name;
  }

  /**
   * @returns {object} the view instance of the previous step
   */
  get previousStepInstance() {
    return this.previousStepObject.view;
  }

  /**
   * @returns {string} the name of the next step
   */
  get nextStepName() {
    return this.nextStepObject.name;
  }

  /**
   * @returns {object} the view instance of the next step
   */
  get nextStepInstance() {
    return this.nextStepObject.view;
  }

  /**
   * Let wizard to navigate to the step according to the given name
   *
   * @param {string} stepName the name of the step
   * @returns {object} the wizard view instance
   * @fires StepsWizard#switch:begin
   * @fires StepsWizard#switch:finish
   * @throws Will throw an error if current step view does not have method setState/getState
   */
  goTo(stepName) {
    /**
     * Switch begin vent.
     *
     * @event StepsWizard#switch:begin
     * @type {object}
     * @property {string} currentStepName
     * @property {string} destStepName the destination step name
     */
    this.trigger('switch:begin', {
      currentStepName: this.currentStepName,
      destStepName: stepName,
    });

    this.currentStepInstance.willHide();

    if (_.isFunction(this.currentStepInstance.getState)) {
      this.state = _.defaults(
        {}, { $lastVisit: this.currentStepName },
        this.currentStepInstance.getState()
      );
    } else {
      throw new Error('Lack of method: getState.');
    }

    this.currentStepIndex = _.findIndex(this.steps, { name: stepName });
    /**
     * Switch finish event
     *
     * @event StepsWizard#switch:finish
     * @type {object}
     * @property {string} stepName
     */
    this.trigger('switch:finish', {
      stepName,
    });

    return this;
  }

  /**
   * Draw the wizard header
   *
   * @private
   * @returns {object} the wizard view instance
   */
  drawHeader() {
    this.$find('.step-header').each((index, element) => {
      const $element = this.$(element);
      const thisStep = this.steps[index].view;
      const titleMap = [
        {
          condition: index < this.currentStepIndex && thisStep.title.done,
          titleObj: thisStep.title.done,
        },
        {
          condition: index === this.currentStepIndex && thisStep.title.active,
          titleObj: thisStep.title.active,
        },
        {
          condition: index > this.currentStepIndex && thisStep.title.pending,
          titleObj: thisStep.title.pending,
        },
        {
          condition: !_.isUndefined(thisStep.title.text) || !_.isUndefined(thisStep.title.html),
          titleObj: thisStep.title,
        },
      ];

      $element.html(this.headerTemplate(_.defaults(
        {},
        _.find(titleMap, _.property('condition'), this).titleObj,
        { showPencil: index < this.currentStepIndex }
      )));
    });

    this.decorateElementWithStepStatus('.step-header');
  }

  decorateElementWithStepStatus(cssClass) {
    this.$find(cssClass).each((index, element) => {
      const $element = this.$(element);

      if (index < this.currentStepIndex) {
        $element.attr('data-step-status', 'done');
      } else if (index === this.currentStepIndex) {
        $element.attr('data-step-status', 'active');
      } else {
        $element.attr('data-step-status', 'pending');
      }
    });
  }

  ensureActions(rawActions) {
    const firstStepFilter = (actions) => {
      if (this.isOnFirstStep) {
        return _.without(actions, 'back');
      }

      return actions;
    };

    const lastStepFilter = (actions) => {
      if (this.isOnLastStep) {
        return _.chain(actions).without('next').union(['save']).value();
      }

      return actions;
    };

    if (_.isEmpty(rawActions)) {
      const actionNames = lastStepFilter(firstStepFilter(['next', 'back', 'cancel']));

      return _.filter(
        this.predefinedActions,
        predefinedAction => _.indexOf(actionNames, predefinedAction.name) >= 0
      );
    }

    return rawActions;
  }

  ensureAction(rawAction) {
    return _.defaults(
      rawAction,
      _.first(_.where(this.predefinedActions, { name: rawAction.name }))
    );
  }

  /**
   * Draw the wizard header
   *
   * @private
   * @returns {object} the wizard view instance
   * @fires StepsWizard#action:begin
   * @fires StepsWizard#action:finish
   */
  drawActionBar() {
    this.$find('.step-action').hide();

    const actions = this.ensureActions(this.currentStepInstance.actions);
    const footer = this.$find(`.step-footer[data-step-name="${this.currentStepName}"]`).empty();

    _.each(actions, (rawAction) => {
      const action = this.ensureAction(rawAction);

      footer.append(this.actionTemplate({
        actionName: action.name,
        displayText: _.isEmpty(action.displayText) ? action.name : action.displayText,
      }));

      const $actionElement = this.$find(`.step-footer[data-step-name="${this.currentStepName}"]  .step-action[data-action-name="${action.name}"]`);

      if (!_.isUndefined(action.htmlAttributes)) {
        if (action.htmlAttributes.class) {
          $actionElement.addClass(action.htmlAttributes.class);
        }

        $actionElement.attr(_.omit(action.htmlAttributes, 'class'));
      }

      $actionElement.prop('disabled', !_.result(action, 'enabled'));
      $actionElement.text(action.displayText);
      $actionElement.unbind().click(() => {
        if (!_.isUndefined(action.method)) {
          /**
           * Action begin event
           *
           * @event StepsWizard#action:begin
           * @type {object}
           * @property {string} [name] the name of the action
           */
          this.trigger('action:begin', { name: action.name });

          const resultInPromise = Promise.resolve(_.isString(action.method)
            ? _.result(this.currentStepInstance, action.method)
            : action.method.apply(this));

          resultInPromise.then(() => {
            /**
             * Action finished event
             *
             * @event StepsWizard#action:finish
             * @type {object}
             * @property {string} [name] the name of the action
             */
            this.trigger('action:finish', { name: action.name });
          });
        }

        return null;
      });
      $actionElement.show();

      this.decorateElementWithStepStatus('.step-footer');
    });

    return this;
  }

  remove(...args) {
    _.each(this.steps, step => step.view.remove());
    super.remove(...args);
  }

  /**
   * Find the jquery element in the context of this steps wizard.
   * This will not return elements in nested steps wizards.
   *
   * @private
   * @param {string} selector jquery selector to match
   * @returns {object} jquery elements matching the selector
   */
  $find(selector) {
    return this.$(selector).not(this.$(`.steps-wizard .steps-wizard ${selector}`));
  }
}
